import React from "react"
import axios from "axios"
import { useNavigate, useLocation } from "react-router-dom"
import { getCookie, setCookie } from "../api/apiUtils"
import getUrl from "api/apiMap"
import useAppState from "context/hooks/useAppState"
import { isExpired } from "react-jwt"
import APP_STATE from "constants/appState.js"

import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from "constants/appState.js"

const invalidNextUrls = ["login", "/password-reset", "/forgot-password", "saml"]

const useRefreshToken = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname
  const { setAppState, appStateRef, openToastNotification } = useAppState()

  const pathnameRef = React.useRef()
  const pathnamePrevRef = React.useRef()

  const isNextUrlValid = (url) => {
    if (url && url !== "/" && invalidNextUrls.every((path) => !url.includes(path))) {
      return true
    }
    return false
  }

  pathnameRef.current = React.useMemo(() => {
    let url = pathname + location.search
    if (isNextUrlValid(url)) {
      localStorage.setItem("prime-next-url", url)
      pathnamePrevRef.current = url
      return url
    }
    return pathnamePrevRef.current
  }, [pathname, location?.search])

  const getAccessToken = () => {
    return getCookie(ACCESS_TOKEN_KEY)
  }

  const getRefreshToken = () => {
    return getCookie(REFRESH_TOKEN_KEY)
  }

  const setTokens = (tokens) => {
    if (tokens?.access) setCookie(ACCESS_TOKEN_KEY, tokens?.access)
    if (tokens?.refresh) setCookie(REFRESH_TOKEN_KEY, tokens?.refresh)
  }

  const deleteTokens = () => {
    document.cookie = `${ACCESS_TOKEN_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
    document.cookie = `${REFRESH_TOKEN_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
  }

  const isAccessTokenExpired = () => {
    let accessToken = getAccessToken()
    if (accessToken) return isExpired(getAccessToken())
    return null
  }

  const isRefreshTokenExpired = () => {
    let refreshToken = getRefreshToken()
    if (refreshToken) return isExpired(getRefreshToken())
    return null
  }

  const getRefreshedToken = async () => {
    if (appStateRef?.current === APP_STATE?.unauthenticated && appStateRef?.current !== undefined) return
    if (appStateRef?.current) {
      appStateRef.current = APP_STATE.refresh
    }

    return await axios
      .post(getUrl("/auth/api/v1/token/refresh/"), {
        refresh: getRefreshToken(),
      })
      .then((resp) => {
        if (appStateRef?.current) {
          appStateRef.current = APP_STATE.authenticated
        }
        return resp
      })
      .catch((err) => {
        if (err.response.data.error.status_code === 401) {
          onSessionExpired()
        }
      })
  }

  const refreshTokens = async () => {
    const rToken = getRefreshToken()

    if (!rToken) {
      onSessionExpired()
      return null
    }

    const resp = await getRefreshedToken()

    if (resp?.data?.access && resp?.data?.refresh) {
      const { access, refresh } = resp.data
      setTokens({
        access,
        refresh,
      })

      if (appStateRef?.current) {
        appStateRef.current = APP_STATE.authenticated
      }
      if (setAppState) {
        setAppState(APP_STATE.authenticated)
      }

      return {
        access,
        refresh,
      }
    }

    return null
  }

  const onSessionExpired = () => {
    navigate("/login", { state: { from: pathnameRef?.current } })
    if (pathname !== "/" && appStateRef?.current !== APP_STATE?.unauthenticated && isRefreshTokenExpired()) {
      if (!getRefreshToken()) {
        openToastNotification("note", "You Have Logged Out", "Please Log In to Continue.")
      } else {
        openToastNotification("error", "Session Timed Out", "Please Log In to Continue.")
      }
    }
    if (appStateRef?.current) {
      appStateRef.current = APP_STATE.unauthenticated
    }
    if (setAppState) {
      setAppState(APP_STATE?.unauthenticated)
    }
  }

  const setNextUrlLocalStorage = () => {
    if (isNextUrlValid(pathnameRef?.current)) {
      localStorage.setItem("prime-next-url", pathnameRef.current)
    }
  }

  return {
    refreshTokens,
    isAccessTokenExpired,
    isRefreshTokenExpired,
    setTokens,
    getAccessToken,
    getRefreshedToken,
    isNextUrlValid,
    pathnameRef,
    onSessionExpired,
    setNextUrlLocalStorage,
    deleteTokens,
    getRefreshToken,
  }
}

export default useRefreshToken
